import { parseCsvRows } from '@/features/datasets/utils/csvPreviewUtils';
import { decodeBase64UnicodeSafe } from '@/features/datasets/utils/filePreviewUtils';
import { MAX_FILE_CHUNK_SIZE } from '@/features/datasets/utils/datasetUploadFlow';
import { getFieldValue } from '@/features/datasets/utils/nestedFieldValue';
import { shouldShowEmptyResponsesChoice } from '@/features/datasets/utils/emptyResponsesHandling';

function entriesToScan(entries, skipEntry) {
    return (entries || []).filter((entry) => entry && !skipEntry?.(entry));
}

/**
 * Full-file scan for empty response values (labels / entities / summaries /
 * outputs) in one column of a picked file, used to decide whether the
 * "How should empty … values be handled?" section is relevant at all.
 *
 * Only files whose entire content is held in the browser can be verified:
 * `useWizardDataUpload.pickFiles` reads the whole file as base64 when it is
 * at most MAX_FILE_CHUNK_SIZE bytes, and only a leading chunk otherwise.
 * Anything that cannot be fully verified (truncated content, parquet,
 * library / Hugging Face picks / hydrated entries without content, parse failures) returns
 * null so callers keep the section visible.
 */

/**
 * True when a response cell counts as empty: missing / null, a blank string,
 * or a list with no meaningful items (e.g. `[]` entities, `[""]` multilabel).
 */
export function isEmptyResponseValue(value) {
    if (value === undefined || value === null) return true;
    if (typeof value === 'string') return !value.trim();
    if (Array.isArray(value)) {
        return value.every(
            (item) => item == null || (typeof item === 'string' && !item.trim())
        );
    }
    return false;
}

/**
 * Parse every record of JSON (array or single object) or JSONL text.
 * Returns null when full parsing cannot be guaranteed (e.g. a broken line).
 */
function parseAllJsonRows(text) {
    try {
        const data = JSON.parse(text);
        if (Array.isArray(data)) return data;
        if (data && typeof data === 'object') return [data];
        return null;
    } catch {
        // Not a single JSON document — fall through to JSONL lines.
    }
    const rows = [];
    for (const line of text.split('\n')) {
        const trimmed = line.trim();
        if (!trimmed) continue;
        try {
            rows.push(JSON.parse(trimmed));
        } catch {
            return null;
        }
    }
    return rows.length ? rows : null;
}

function scanCsv(text, fieldName) {
    const rows = parseCsvRows(text, Infinity);
    if (rows.length < 2) return null;
    const headers = rows[0].map((h) => (h ?? '').trim());
    const idx = headers.indexOf(fieldName);
    if (idx === -1) return null;
    for (let i = 1; i < rows.length; i++) {
        const row = rows[i];
        if (idx >= row.length || isEmptyResponseValue(row[idx])) return true;
    }
    return false;
}

function scanJson(text, fieldName) {
    const rows = parseAllJsonRows(text);
    if (!rows) return null;
    const records = rows.filter((row) => row && typeof row === 'object');
    if (!records.length) return null;
    for (const row of records) {
        if (isEmptyResponseValue(getFieldValue(row, fieldName))) return true;
    }
    return false;
}

/**
 * Scan every record of a picked file for empty values in `fieldName`.
 *
 * @returns {boolean|null}
 *   true  — at least one record has an empty value in the column,
 *   false — the whole file was scanned and no empty values were found,
 *   null  — cannot verify (truncated content, unsupported format, parse
 *           failure); treat as "might contain empty values".
 */
export function scanFileForEmptyFieldValues({ base64Content, fileType, fieldName, sizeBytes }) {
    if (!base64Content || !fieldName) return null;
    // Content is only the leading chunk when the file exceeds
    // MAX_FILE_CHUNK_SIZE — a partial scan cannot prove there are no empties.
    if (sizeBytes == null || sizeBytes > MAX_FILE_CHUNK_SIZE) return null;

    const type = (fileType || '').toLowerCase();
    if (type !== 'csv' && type !== 'json' && type !== 'jsonl') return null;

    const decoded = decodeBase64UnicodeSafe(base64Content);
    if (!decoded) return null;

    try {
        return type === 'csv' ? scanCsv(decoded, fieldName) : scanJson(decoded, fieldName);
    } catch {
        return null;
    }
}

/**
 * Scan wizard upload entries for empty values in ``column``.
 *
 * @returns {boolean|null}
 *   true  — at least one file has an empty value,
 *   false — every file was fully scanned and none has empties,
 *   null  — at least one file could not be verified and none had a definite empty.
 */
export function scanUploadEntriesForEmptyField({
    entries,
    column,
    fileCache = {},
    libraryEmptyScans = {},
    skipEntry,
}) {
    if (!column || !Array.isArray(entries) || entries.length === 0) return null;
    const scannable = entriesToScan(entries, skipEntry);
    // Generate-with-AI chips have no file bytes; skip them rather than treating
    // the missing scan as "might have empties" (that would show the radios).
    if (!scannable.length) return false;
    let unverifiable = false;
    for (const entry of scannable) {
        const key = `${entry.id}::${column}`;
        let result;
        if (entry.fromLibrary || entry.fromHuggingFace) {
            result = Object.prototype.hasOwnProperty.call(libraryEmptyScans, key)
                ? libraryEmptyScans[key]
                : null;
        } else if (!entry.content) {
            result = null;
        } else {
            if (!(key in fileCache)) {
                fileCache[key] = scanFileForEmptyFieldValues({
                    base64Content: entry.content,
                    fileType: entry.format,
                    fieldName: column,
                    sizeBytes: entry.sizeBytes,
                });
            }
            result = fileCache[key];
        }
        if (result === true) return true;
        if (result !== false) unverifiable = true;
    }
    return unverifiable ? null : false;
}

/**
 * Whether Data Collection should show "How should empty … values be handled?".
 *
 * Hide while columns are still loading or a library / Hugging Face empty-scan
 * has not answered yet, so the radios do not flash on then off. Hide when
 * every file is verified to have no empties. Show when empties exist, or when
 * a completed scan is unverifiable (truncated file, parquet, failed request).
 */
export function shouldShowEmptyResponsesSection({
    entries,
    column,
    fileCache = {},
    libraryEmptyScans = {},
    columnsLoading = false,
    skipEntry,
}) {
    if (columnsLoading) return false;
    if (!column || !Array.isArray(entries) || !entries.length) return false;
    const scannable = entriesToScan(entries, skipEntry);
    if (!scannable.length) return false;
    const pendingRemoteScan = scannable.some((entry) => {
        if (!entry?.fromLibrary && !entry?.fromHuggingFace) return false;
        if (!entry.id) return true;
        return !Object.prototype.hasOwnProperty.call(
            libraryEmptyScans,
            `${entry.id}::${column}`
        );
    });
    if (pendingRemoteScan) return false;
    return shouldShowEmptyResponsesChoice({
        hasLabelColumn: true,
        emptyValuesCheckSettled: true,
        hasEmptyValues: scanUploadEntriesForEmptyField({
            entries: scannable,
            column,
            fileCache,
            libraryEmptyScans,
        }),
    });
}
